import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TrendingProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@tCentro/Components/CENProductCarouselProduct";
import { buildCarouselItemList, buildItemList } from "@tCentro/Custom/Utilities/dataLayerEvents";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import useEmblaCarousel from "embla-carousel-react";
import * as React from "react";
import { connect, DispatchProp, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    title: string;
    carouselType: string;
    displayProductsFrom: string;
    selectedCategoryIds: string[];
    relatedProductType: string;
    numberOfProductsToDisplay: number;
    seedWithManuallyAssigned: string;
    maxNumberOfColumns: number;
    showImage: boolean;
    showTitle: boolean;
    showBrandName: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showProductDescription: boolean;
    id: string;
    itemListTitle?: string;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        products: getProductsForProductInfoList(state, ownProps.id),
        errorMessage: getErrorMessage(state, ownProps.id),
        pageType: getCurrentPage(state).type,
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface ProductCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    customproducttilewrapper?: InjectableCss;
    wrapper?: InjectableCss;
    carouselProductWrapper?: InjectableCss;
    messageStyles?: InjectableCss;
}

export const productCarouselStyles: ProductCarouselStyles = {
    messageStyles: {
        css: css`
            color: ${getProp("theme.typography.p.color")};
            font-size: 24px;
            font-weight: 600;
            line-height: 32px;
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 40px;
            margin-top: 55px;
            color: ${getProp("theme.typography.h2.color")};
            font-weight: 700;
            font-size: 32px !important;
            line-height: 40px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-weight: 600;
                            font-size: 24px !important;
                            line-height: 32px;
                            margin-bottom: 24px;
                            margin-top: 34px;
                        `,
                        css`
                            font-weight: 600;
                            font-size: 24px !important;
                            line-height: 32px;
                            margin-bottom: 24px;
                            margin-top: 34px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            max-width: 100% !important;
        `,
    },

    customproducttilewrapper: {
        css: css`
            margin: 0 auto;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-bottom: 30px;
                        `,
                        css`
                            padding-bottom: 30px;
                        `,
                        css`
                            padding-bottom: 30px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            margin-top: 5px;
            @media all and (max-width: 1159px) and (min-width: 992px) {
                width: 50% !important;
            }
            @media all and (max-width: 767px) and (min-width: 576px) {
                width: 100% !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 2px;
            display: flex;
            flex-direction: column;
            &.recently-viewedTile {
                > div[class^="sc"] {
                    border: 1px solid #d2d0cd;
                    position: relative;
                    padding: 24px 8px 50px 18px;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    a[data-test-selector="productImage"] {
                        align-items: flex-start;
                        background: #ffffff;
                        box-shadow:
                            0px 0px 1px rgba(38, 37, 37, 0.56),
                            0px 2px 8px rgba(38, 37, 37, 0.16);
                    }
                    > div[class^="sc"] {
                        [data-test-selector="saleprice-label"] {
                            font-size: ${TYPOGRAPHY.size.base};
                            padding-right: 5px;
                        }
                        [data-test-selector="productPrice_unitNetPrice"] {
                            font-size: ${TYPOGRAPHY.size.base};
                            span {
                                font-size: ${TYPOGRAPHY.size.smaller};
                            }
                        }
                        a[class^="StyledButton"] {
                            > span[class^="TypographyStyle"] {
                                font-size: ${TYPOGRAPHY.size.smaller};
                            }
                        }
                    }
                }
            }
        `,
    },
    wrapper: {},
    carouselProductWrapper: {
        css: css`
            box-shadow: 0px 0px 10px #0000000b;
            border: 1px solid #e6e6e6;
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            position: relative;
            padding-top: 15px;
            background: #f2f2f2;
            a[data-test-selector="productImage"] {
                justify-content: center;
                flex-grow: 1;
                ~ div {
                    flex-grow: 0;
                    padding-top: 16px;
                }
                > div {
                    > img {
                        //max-width: 250px;
                        max-height: 250px;
                    }
                }
                > img {
                    //max-width: 250px;
                    max-height: 250px;
                    object-fit: contain;
                    height: auto;
                    aspect-ratio: 1/1;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-top: 38px;
                    `,
                    css`
                        padding-top: 38px;
                    `,
                    css`
                        padding-top: 38px;
                    `,
                    css`
                        padding-top: 38px;
                    `,
                    null,
                ])}
        `,
    },
    carouselProductStyles: {
        productQuantityOrderedStyles: {
            cssOverrides: {
                formField: css`
                    margin-top: 0;
                    width: 60%;
                    @media all and (max-width: 1400px) and (min-width: 1160px) {
                        input {
                            width: 104px !important;
                        }
                    }
                    input:focus {
                        border-radius: 40px !important;
                    }
                `,
            },
        },
        productAddToCartButtonStyles: {
            css: css`
                width: 100%;
                border-radius: 16px;
                padding: 8px 16px;
                span {
                    font-size: 10px !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            span {
                                font-size: 12px;
                            }
                        `,
                        css`
                            span {
                                font-size: 12px;
                            }
                        `,
                        css`
                            span {
                                font-size: 10px;
                            }
                        `,
                        css`
                            span {
                                font-size: 10px;
                            }
                        `,
                        css``,
                    ])}
            `,
            typographyProps: {
                size: 14,
            },
        },
    },
};

const styles = productCarouselStyles;

const CENFBTNewCartProductCarousel: React.FC<Props> = ({
    id,
    products,
    errorMessage,
    loadCarouselProducts,
    pageType,
    carouselType,
    relatedProductType,
    seedWithManuallyAssigned,
    selectedCategoryIds,
    numberOfProductsToDisplay,
    maxNumberOfColumns,
    showAddToCart,
    showAddToList,
    showBrandName,
    displayProductsFrom,
    showImage,
    showPartNumbers,
    showProductDescription,
    showTitle,
    showPrice,
    title,
    shellContext,
    theme,
    itemListTitle
}) => {
    const afterCarousel = React.createRef<HTMLSpanElement>();
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = React.useContext(BrandStateContext).value;
    const category = React.useContext(CategoryContext);

    const productContext = React.useContext(ProductContext);
    React.useEffect(() => {
        loadCarouselProducts({
            carouselId: id,
            carouselType,
            relatedProductType,
            seedWithManuallyAssigned,
            displayProductsFrom,
            selectedCategoryIds,
            numberOfProductsToDisplay,
            isProductDetailsPage,
            productId: productContext?.product?.id,
            isProductListPage,
            category,
            isBrandDetailsPage,
            brand,
        });
    }, [
        carouselType,
        relatedProductType,
        seedWithManuallyAssigned,
        displayProductsFrom,
        selectedCategoryIds,
        numberOfProductsToDisplay,
        productContext?.product?.id,
        category?.id,
        brand?.id,
    ]);

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfColumnsValue = maxNumberOfColumns || 6;

        if (typeof window === "undefined") {
            return maxNumberOfColumnsValue;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[4]) {
            localSlidesToScroll = 4;
        } else if (window.innerWidth < theme.breakpoints.values[5]) {
            localSlidesToScroll = 5;
        } else {
            localSlidesToScroll = 6;
        }

        return Math.min(localSlidesToScroll, maxNumberOfColumnsValue);
    }, [maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    const onWindowResize = () => {
        setWindowResizeTime(Date.now());
    };
    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [maxNumberOfColumns, windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !products || products.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: draggable,
            loop: products.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, products]);

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    const message = siteMessage("NoProducts_FBTProductCarouselMessage");
    if (!products || products.length === 0) {
        return (
            <StyledWrapper
                css={css`
                    width: 100%;
                    display: flex;
                    height: 200px;
                    justify-content: center;
                    align-items: center;
                    background: ${COLORS.background.grayShade};
                    margin-top: 15px;
                `}
            >
                <Typography {...styles.messageStyles}>{message}</Typography>
            </StyledWrapper>
        );
    }

    const carouselTitle =
        title ||
        translate("{0}", carouselTypeOptions.find(o => o.value === carouselType)?.displayName || "Product Carousel");
    const showBrandBlocks = showBrandName && products.some(o => !!o.brand);

        
const carouselItemList = buildCarouselItemList(
    carouselType,
    itemListTitle || title,
    carouselTypeOptions.find(o => o.value === carouselType)?.displayName,
);
    return (
        <StyledWrapper {...styles.wrapper} className="recently-viewed">
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            <StyledWrapper {...styles.customproducttilewrapper}>
                <Typography {...styles.titleText}>{carouselTitle}</Typography>
                <GridContainer
                    {...styles.mainContainer}
                    data-test-selector={`productCarousel_${carouselType}${relatedProductType}`}
                >
                    <GridItem {...styles.prevArrowGridItem}>
                        {products.length > slidesToScroll && (
                            <Button
                                {...styles.prevArrowButton}
                                onClick={() => embla && embla.scrollPrev()}
                                disabled={!canScrollPrev}
                                data-test-selector="prevBtn"
                            >
                                <ButtonIcon src={ChevronLeft} />
                            </Button>
                        )}
                    </GridItem>
                    <GridItem {...styles.carouselGridItem}>
                        <StyledWrapper {...styles.carouselWrapper}>
                            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                    {products.map(product => (
                                        <StyledWrapper
                                            {...styles.carouselSlide}
                                            key={product.id}
                                            style={{ width: `calc(100% / ${slidesToScroll})` }}
                                        >
                                            <StyledWrapper
                                                {...styles.carouselSlideInner}
                                                id={`product_${product.id}`}
                                                data-test-selector="productContainer"
                                                className="recently-viewedTile"
                                            >
                                                <StyledWrapper {...styles.carouselProductWrapper}>
                                                    <TrendingProductCarouselProduct
                                                        carouselId={id}
                                                        product={product}
                                                        showImage={showImage}
                                                        showBrand={showBrandBlocks}
                                                        showTitle={showTitle}
                                                        showPartNumbers={showPartNumbers}
                                                        showPrice={showPrice}
                                                        showAddToCart={showAddToCart}
                                                        showAddToList={showAddToList}
                                                        showProductDescription={showProductDescription}
                                                        extendedStyles={styles.carouselProductStyles}
                                                        itemListOverride={carouselItemList}
                                                    />
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    ))}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.nextArrowGridItem}>
                        {products.length > slidesToScroll && (
                            <Button
                                {...styles.nextArrowButton}
                                onClick={() => embla && embla.scrollNext()}
                                disabled={!canScrollNext}
                                data-test-selector="nextBtn"
                            >
                                <ButtonIcon src={ChevronRight} />
                            </Button>
                        )}
                    </GridItem>
                </GridContainer>
            </StyledWrapper>

            <span ref={afterCarousel} tabIndex={-1} />
        </StyledWrapper>
    );
};

const carouselTypeOptions = [
    { value: "crossSells", displayName: "Cross Sells" },
    { value: "customersAlsoPurchased", displayName: "Customers Also Purchased" },
    { value: "recentlyViewed", displayName: "Recently Viewed" },
    { value: "relatedProducts", displayName: "Related Products" },
    { value: "topSellers", displayName: "Top Sellers" },
];

export default connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(CENFBTNewCartProductCarousel)));
